import React, { useCallback, useEffect, useState } from 'react';
import {
  alpha,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Add from '@mui/icons-material/Add';
import GetAppOutlined from '@mui/icons-material/GetAppOutlined';
import Remove from '@mui/icons-material/Remove';
import ZoomIn from '@mui/icons-material/ZoomIn';
import classNames from 'classnames';
import { Events } from '../../enums/Analytics';
import Analytics, { EventProperty } from '../../tools/Analytics';
import CardOverlayBadge from '../Card/CardOverlayBadge';
import FullScreenDialog from '../Dialog/FullScreenDialog';
import ImageViewer, { ImageViewerProps } from './ImageViewer';

const DEFAULT_ZOOM = 1;
const ZOOM_STEP = 0.5;

type MovementEvent = React.MouseEvent<Element> | React.TouchEvent<Element>;

interface MouseEventCoordinates {
  x: number;
  y: number;
}

const useStyles = makeStyles(() => ({
  appBar: {
    borderBottom: '1px solid rgba(219, 219, 219, 0.2)',
    color: 'white',
  },
  disabled: {
    '&.Mui-disabled': {
      color: alpha('#fff', 0.5),
    },
  },
  divider: {
    borderColor: 'var(--divider-color-light-dark)',
    opacity: 0.2,
  },
  imageContainer: {
    alignItems: 'center',
    display: 'flex',
    flex: '1',
    height: '100%',
    justifyContent: 'center',
    maxHeight: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
    position: 'relative',
  },
  image: {
    maxHeight: '90%',
    maxWidth: '90%',
    transformOrigin: 'center center',
  },
  zoomIn: {
    cursor: 'zoom-in',
  },
  zoomOut: {
    cursor: 'zoom-out',
  },
}));

export interface FullScreenImageViewerProps extends ImageViewerProps {
  downloadTrigger?: number;
  onFullScreenChange?: (fullScreen: boolean) => void;
  title?: string;
  trackingProperties?: EventProperty;
  zoomable?: boolean;
}

const FullScreenImageViewer = React.forwardRef<
  HTMLDivElement,
  FullScreenImageViewerProps
>((props: FullScreenImageViewerProps, ref): JSX.Element => {
  const classes = useStyles();

  const {
    downloadable,
    downloadTrigger,
    height,
    onDownload,
    onFullScreenChange,
    title = '',
    trackingProperties,
    url,
    width,
    zoomable,
    ...imageViewerProps
  } = props;

  const [enlarge, setEnlarge] = useState(false);
  const [ready, setReady] = useState(!downloadable);
  const [moving, setMoving] = useState(false);

  const [translateX, setTranslateX] = useState(0);
  const [startingTranslateX, setStartingTranslateX] = useState(0);
  const [latestTranslateX, setLatestTranslateX] = useState(0);

  const [translateY, setTranslateY] = useState(0);
  const [startingTranslateY, setStartingTranslateY] = useState(0);
  const [latestTranslateY, setLatestTranslateY] = useState(0);

  const [zoom, setZoom] = useState(DEFAULT_ZOOM);

  useEffect(() => {
    if (enlarge) {
      setReady(!downloadable);
      setMoving(false);
      setTranslateX(0);
      setStartingTranslateX(0);
      setLatestTranslateX(0);
      setTranslateY(0);
      setStartingTranslateY(0);
      setLatestTranslateY(0);
      setZoom(DEFAULT_ZOOM);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enlarge]);

  useEffect(() => {
    if (downloadable && downloadTrigger) {
      handleDownload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadable, downloadTrigger]);

  const getMouseEventCoordinates = useCallback(
    (e: MovementEvent): MouseEventCoordinates => {
      try {
        const { touches } = e as React.TouchEvent<Element>;
        if (touches && touches[0]) {
          const firstTouch = touches[0];
          const { pageX: x, pageY: y } = firstTouch;
          return { x, y };
        }

        const { pageX: x, pageY: y } = e as React.MouseEvent<Element>;
        return { x, y };
      } catch (error) {
        return { x: 0, y: 0 };
      }
    },
    []
  );

  const toggleEnlarge = () => {
    const shouldEnlarge = !enlarge;
    setEnlarge(shouldEnlarge);

    if (shouldEnlarge) {
      Analytics.track(Events.IMAGE_ENLARGED, trackingProperties);
    }

    if (onFullScreenChange) {
      onFullScreenChange(shouldEnlarge);
    }
  };

  const onLoad = () => {
    setReady(true);
  };

  const onZoom = (delta: number) => {
    const deltaZoom = zoom + delta * ZOOM_STEP;
    const newZoom = deltaZoom < DEFAULT_ZOOM ? DEFAULT_ZOOM : deltaZoom;

    setZoom(newZoom);

    if (newZoom === DEFAULT_ZOOM) {
      setTranslateX(0);
      setTranslateY(0);
    }
  };

  const onStartMovement = (e: MovementEvent) => {
    // don't allow movement if there's no zoom, as it doesn't make sense
    // to just move the image around, so we zoom in instead
    if (zoom === 1) {
      // prevent zooming if the image is right-clicked
      if ((e as React.MouseEvent).button === 0) {
        onZoom(1);
      }

      return;
    }

    setMoving(true);

    // the image movement (drag) is relative to where the user clicked, so we need
    // to keep these coordinates when the user clicks on the image to start moving
    const { x, y } = getMouseEventCoordinates(e);
    setStartingTranslateX(x - latestTranslateX);
    setStartingTranslateY(y - latestTranslateY);
  };

  const onMovement = (e: MovementEvent) => {
    if (!moving) {
      return;
    }

    const { x, y } = getMouseEventCoordinates(e);
    const newTranslateX = x - startingTranslateX;
    const newTranslateY = y - startingTranslateY;

    setTranslateX(newTranslateX);
    setLatestTranslateX(newTranslateX);

    setTranslateY(newTranslateY);
    setLatestTranslateY(newTranslateY);
  };

  const onEndMovement = () => {
    setMoving(false);
  };

  const handleDownload = () => {
    if (onDownload) {
      onDownload();
    }

    window.open(url, '_blank');
    Analytics.track(Events.CONTENT_DOWNLOADED, trackingProperties);
  };

  const renderDownloadButton = (): JSX.Element | undefined => {
    if (!downloadable) {
      return undefined;
    }

    return (
      <>
        <Divider className={classes.divider} flexItem orientation="vertical" />
        <Box pl={1.5}>
          <Tooltip
            arrow
            PopperProps={{ disablePortal: true }}
            title="Download"
            enterTouchDelay={0}
            leaveTouchDelay={5000}
          >
            <IconButton
              aria-label="download"
              color="inherit"
              download
              edge="end"
              href={url}
              onClick={handleDownload}
              target="_blank"
              size="large"
            >
              <GetAppOutlined />
            </IconButton>
          </Tooltip>
        </Box>
      </>
    );
  };

  const renderImageComponent = (): JSX.Element => {
    const style = {
      cursor: zoom > DEFAULT_ZOOM ? 'grab' : 'zoom-in',
      transform: `scale(${zoom}) translate(${translateX}px, ${translateY}px)`,
    };

    const listeners = {
      onMouseDown: onStartMovement,
      onMouseLeave: onEndMovement,
      onMouseMove: onMovement,
      onMouseUp: onEndMovement,
      onTouchEnd: onEndMovement,
      onTouchMove: onMovement,
      onTouchStart: onStartMovement,
    };

    if (!downloadable) {
      return (
        <div
          className={classes.image}
          style={{
            background: `url(${url}) no-repeat scroll`,
            backgroundPosition: 'center',
            backgroundSize: 'contain',
            height,
            width,
            ...style,
          }}
          {...listeners}
        />
      );
    }

    return (
      <img
        alt={imageViewerProps.name}
        className={classes.image}
        draggable={false}
        onLoad={onLoad}
        src={url}
        style={style}
        {...listeners}
      />
    );
  };

  const background = 'var(--background-color-black-pastel)';

  return (
    <>
      <Box className={classes.imageContainer} position="relative">
        <ImageViewer
          ref={ref}
          className={classNames({ [classes.zoomIn]: zoomable })}
          downloadable={downloadable}
          height={height}
          onClick={zoomable ? toggleEnlarge : undefined}
          url={url}
          width={width}
          {...imageViewerProps}
        >
          {zoomable && (
            <Box position="absolute" bottom={0} right={0} zIndex={1}>
              <CardOverlayBadge>
                <ZoomIn />
              </CardOverlayBadge>
            </Box>
          )}
        </ImageViewer>
      </Box>
      <FullScreenDialog
        classes={{ appBar: classes.appBar, divider: classes.divider }}
        LayoutAppBarProps={{ background }}
        LayoutContentProps={{ background }}
        onClose={toggleEnlarge}
        open={enlarge}
        title={title}
        primaryAction={
          <>
            <Tooltip
              arrow
              PopperProps={{ disablePortal: true }}
              title="Zoom Out"
              enterTouchDelay={0}
              leaveTouchDelay={5000}
            >
              <Box>
                <IconButton
                  aria-label="zoom out"
                  classes={{
                    disabled: classes.disabled,
                  }}
                  disabled={zoom === DEFAULT_ZOOM}
                  color="inherit"
                  onClick={() => onZoom(-1)}
                  size="large"
                >
                  <Remove />
                </IconButton>
              </Box>
            </Tooltip>
            <Box pr={downloadable ? 1.5 : 0}>
              <Tooltip
                arrow
                PopperProps={{ disablePortal: true }}
                title="Zoom In"
                enterTouchDelay={0}
                leaveTouchDelay={5000}
              >
                <IconButton
                  aria-label="zoom in"
                  color="inherit"
                  onClick={() => onZoom(1)}
                  size="large"
                >
                  <Add />
                </IconButton>
              </Tooltip>
            </Box>
            {renderDownloadButton()}
          </>
        }
      >
        {enlarge && (
          <Box {...{ ref }} className={classes.imageContainer}>
            {!ready && (
              <Box color="white" position="absolute" textAlign="center">
                <CircularProgress color="secondary" />
                <Typography color="inherit" component="div" variant="caption">
                  Loading image...
                </Typography>
              </Box>
            )}
            {renderImageComponent()}
          </Box>
        )}
      </FullScreenDialog>
    </>
  );
});

export default FullScreenImageViewer;
